import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { FC } from 'react';
import { BillingProcess } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { ProcessStatusIndicator, isActiveStatus } from './ProcessStatusIndicator';
import { ProcessProgressMessage } from './ProcessProgressMessage';
import { formatDateString } from '../../../util/dateFormat';
import { getClinicNameByClinicId } from '../../../lib/clinic';
import styles from './ProcessStatusHeader.module.css';

interface ProcessStatusHeaderProps {
  process?: BillingProcess | null;
  clinics: IClinic[];
  onRefresh?: (processId: number) => void;
  isRefreshing?: boolean;
}

export const ProcessStatusHeader: FC<ProcessStatusHeaderProps> = ({
  process,
  clinics,
  onRefresh,
  isRefreshing = false,
}) => {
  const handleRefresh = () => {
    if (process?.id && onRefresh) {
      onRefresh(process.id);
    }
  };

  return (
    <Box className={styles.processCard}>
      {/* Process Title and Status */}
      <Box className={styles.processInfo}>
        <Typography variant="h5" className={styles.processTitle}>
          {process ? `Process #${process.id}` : 'New Process'}
        </Typography>
        {process && (
          <Box className={styles.processActions}>
            {isActiveStatus(process.status) && (
              <IconButton 
                size="small" 
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Refresh process status"
              >
                <RefreshIcon />
              </IconButton>
            )}
            <ProcessStatusIndicator 
              status={process.status}
              timestamp={process.lastModifiedAt}
              showTimestamp
            />
          </Box>
        )}
      </Box>

      {/* Process Details */}
      {process && (
        <Box className={styles.processDetails}>
          <Typography variant="body2" color="text.secondary">
            {process.creationParameters?.filters?.clinicIds?.length > 0 && (
              <span>
                <strong>Clinic filter:</strong>{' '}
                {process.creationParameters.filters.clinicIds.map((clinicId, index) => (
                  <span key={clinicId}>
                    {index > 0 && ', '}
                    <Chip
                      label={getClinicNameByClinicId(clinicId, clinics)}
                      size="small"
                      variant="outlined"
                      color="primary"
                      className={styles.chipStyle}
                    />
                  </span>
                ))}
                {' • '}
              </span>
            )}
            <strong>Created by:</strong> {process.startedByUserFullName || 'Unknown'} • 
            <strong> Created at:</strong> {formatDateString(process.createdAt)} • 
            <strong> Last modified by:</strong> {process.lastModifiedByUserFullName || 'Unknown'} • 
            <strong> Last modified at:</strong> {formatDateString(process.lastModifiedAt)}
          </Typography>
        </Box>
      )}

      {/* Show progress message for active processes */}
      {process && isActiveStatus(process.status) && (
        <Box className={styles.progressMessage}>
          <ProcessProgressMessage
            status={process.status}
            processId={process.id}
            startedAt={process.createdAt}
            lastUpdated={process.lastModifiedAt}
            showDetails={false}
          />
        </Box>
      )}
    </Box>
  );
};